/**
 * PnL distribution histogram showing trade profit/loss distribution.
 * Green bars for profitable buckets, red bars for loss buckets.
 * Includes reference lines for mean and median PnL values.
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { formatCurrency } from '@/utils/formatters';
import type { PnLBucket } from '@/types/performance';

interface PnLDistributionChartProps {
  data: PnLBucket[];
  winRate: number;
  meanPnl: number;
  medianPnl: number;
  isLoading?: boolean;
}

export function PnLDistributionChart({
  data,
  winRate,
  meanPnl,
  medianPnl,
  isLoading = false,
}: PnLDistributionChartProps) {
  if (isLoading) {
    return <div className="h-48 rounded shimmer" data-testid="chart-skeleton" />;
  }

  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-dark-700 bg-dark-800/50">
        <p className="text-sm text-dark-500">No closed positions for PnL analysis</p>
      </div>
    );
  }

  return (
    <div>
      <p className="mb-2 text-lg font-semibold text-dark-100">
        Win Rate: {winRate.toFixed(1)}%
      </p>

      <div className="h-48" role="img" aria-label="PnL distribution histogram">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis
              dataKey="rangeStart"
              tickFormatter={(value) => formatCurrency(value)}
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={{ stroke: '#334155' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickLine={false}
              axisLine={false}
              width={40}
              allowDecimals={false}
            />
            <Tooltip content={<PnLDistributionTooltip />} />
            <ReferenceLine
              x={meanPnl}
              stroke="#94a3b8"
              strokeDasharray="3 3"
              label={{ value: 'Mean', position: 'top', fill: '#94a3b8', fontSize: 11 }}
            />
            <ReferenceLine
              x={medianPnl}
              stroke="#64748b"
              strokeDasharray="3 3"
              label={{ value: 'Median', position: 'top', fill: '#64748b', fontSize: 11 }}
            />
            <Bar dataKey="count" radius={[2, 2, 0, 0]}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.isProfit ? '#10b981' : '#f43f5e'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Accessible data table */}
      <table className="sr-only">
        <caption>PnL distribution data</caption>
        <thead>
          <tr>
            <th>Range</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {data.map((bucket, index) => (
            <tr key={index}>
              <td>
                {formatCurrency(bucket.rangeStart)} to {formatCurrency(bucket.rangeEnd)}
              </td>
              <td>{bucket.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TooltipPayloadEntry {
  payload: PnLBucket;
}

interface PnLDistributionTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
}

function PnLDistributionTooltip({ active, payload }: PnLDistributionTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const bucket = payload[0]!.payload;

  return (
    <div
      style={{
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        padding: '8px 12px',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.3)',
      }}
    >
      <p style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>
        {formatCurrency(bucket.rangeStart)} to {formatCurrency(bucket.rangeEnd)}
      </p>
      <p style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: 500 }}>
        {bucket.count} {bucket.count === 1 ? 'trade' : 'trades'}
      </p>
    </div>
  );
}
